import { Box, FlexBox, Inline } from '@ardoq/layout';
import { TableFilterPillView } from '@ardoq/table-filter-bar';
import { t, type I18nLocale } from '@ardoq/i18n';
import type { InventoryFilterPillsCommands } from './types';
import { SmallTertiaryButton } from '@ardoq/button';
import styled from 'styled-components';
import { colors, space } from '@ardoq/design-tokens';
import { tourAnchorIds } from '@ardoq/product-tours';

const Separator = styled.div`
  align-self: stretch;
  width: 1px;
  margin: ${space.xsmall} 0;
  background-color: ${colors.borderSubtle};
`;

export type InventoryFilterPill = {
  columnKey: string;
  columnLabel: string;
  operatorLabel: string;
  selection: string | null; // operators isEmpty and isNotEmpty don't use value
};

type InventoryFilterBarProps = {
  filterPills: InventoryFilterPill[];
  commands: InventoryFilterPillsCommands;
  i18nLocale: I18nLocale;
};

export const InventoryFilterBar = ({
  filterPills,
  commands,
  i18nLocale,
}: InventoryFilterBarProps) => {
  // extra space between top row and table
  if (filterPills.length === 0) return <FlexBox />;

  return (
    <Inline gap="small" data-tour-id={tourAnchorIds.inventoryFilterBar}>
      {/* Copies of one field can carry the same filter, so only the rule's
          position tells the pills apart. They hold no state and are derived
          fresh from the subquery on every emission. */}
      {filterPills.map((pill, index) => (
        <Box
          key={index}
          role="group"
          aria-label={t(
            i18nLocale,
            'inventoryApp:filterBar.filterGroupLabel',
            { columnLabel: pill.columnLabel },
            'Filter for {{columnLabel}}'
          )}
          minWidth="0" // needed to handle overflow on children properly
        >
          <TableFilterPillView
            label={{ label: pill.columnLabel }}
            operator={{
              type: 'readonly',
              children: pill.operatorLabel,
            }}
            value={pill.selection}
            onRemove={() => commands.clearColumnFilter(pill.columnKey)}
            removeFilterAriaLabel={t(
              i18nLocale,
              'inventoryApp:filterBar.removeFilter',
              'Remove filter'
            )}
          />
        </Box>
      ))}
      <Separator />
      <SmallTertiaryButton onClick={commands.clearAllFilters}>
        {t(i18nLocale, 'inventory:search.clearAllFilters', 'Clear all filters')}
      </SmallTertiaryButton>
    </Inline>
  );
};
